import { forEach, isEmpty, isEqual, uniqBy, xor } from 'lodash';
import * as React from 'react';
import { RefObject } from 'react';
import * as ReactDOM from 'react-dom';

import { PredictorExpanderMapContainer } from '../../containers/PredictorExpanderMapContainer/PredictorExpanderMapContainer';
import { Bem } from '../../service/Bem/Bem';
import { YandexMapHelper } from '../../service/YandexMapHelper/YandexMapHelper';
import {
    CheckBoxChangeData,
    DisplayListItem,
    DisplayPoint,
    DisplayRegion,
    MapType,
    PointColor,
    Screen,
    SelectedScreens
} from '../../typings/displayMap';
import { DisplayMapBalloon } from '../DisplayMapBalloon/DisplayMapBalloon';
import { MapPolygonsDrawer } from '../MapPolygonsDrawer/MapPolygonsDrawer';
import { PlaceMarkIndoor } from '../PlaceMarkIndoor/PlaceMarkIndoor';
import { PlaceMarkOutdoor } from '../PlaceMarkOutdoor/PlaceMarkOutdoor';

import './DisplayMap.css';

export interface Platforms {
    [id: string]: DisplayPoint[];
}

export interface DisplayMapProps {
    mapType: MapType;
    regions: DisplayRegion[];
    platforms: Platforms;
    selectedScreens: SelectedScreens;
    zoomToRegion?: string;
    showAddressPoint: boolean;
    zoomToGroup?: DisplayListItem;
    onPlatformClick: (groupId: string[]) => void;
    onBalloonCaptionClick: (groupId: string) => void;
    onScreensSelect: (data: CheckBoxChangeData) => void;
    onMapLoaded: (onMapLoaded: boolean) => void;
    drawPolygonsDrawer?: boolean;
    isNewInterface?: boolean;
}

export interface DisplayMapState {}

export class DisplayMap extends React.Component<
    DisplayMapProps,
    DisplayMapState
> {
    private block = 'display-map';
    private yaMap: ymaps.Map;
    private mapRef: RefObject<HTMLDivElement> = React.createRef();
    private regionManager: { [id: string]: ymaps.ObjectManager } = {};
    private templates: {} = {};
    private regionCenter: {} = {};
    private addressPoint: ymaps.GeoObject;
    private pointGeometry: ymaps.geometry.Point;
    private clusterize = true;

    render() {
        const { mapType, isNewInterface } = this.props;
        return (
            <div className={Bem.cls(this.block)}>
                <div ref={this.mapRef} className={Bem.cls(this.block, 'map')}>
                    {this.props.drawPolygonsDrawer && this.yaMap && (
                        <MapPolygonsDrawer map={this.yaMap} />
                    )}
                    {isNewInterface && (
                        <div className={Bem.cls(this.block, 'predictor')}>
                            <PredictorExpanderMapContainer mapType={mapType} />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    componentDidMount() {
        YandexMapHelper.initApiMap().then(() => {
            ymaps.ready(() => {
                this.createMap();
                this.buildPoints();
                this.initMapEvents();
            });
        });
    }

    componentDidUpdate(prevProps: DisplayMapProps, prevState: DisplayMapState) {
        const {
            platforms,
            selectedScreens,
            regions,
            zoomToRegion,
            zoomToGroup,
            showAddressPoint
        } = this.props;

        this.compareZoomToRegion(prevProps.zoomToRegion, zoomToRegion);

        if (zoomToGroup) {
            this.zoomToGroup(zoomToGroup);
        }

        if (showAddressPoint) {
            this.hideAddressPoint();
        }

        // сравнение по всем регионам
        this.comparePlatforms(
            prevProps.platforms,
            platforms,
            uniqBy([...regions, ...prevProps.regions], 'id')
        );
        this.compareSelectedScreens(prevProps.selectedScreens, selectedScreens);
    }

    private compareZoomToRegion = (prev: string = '', current: string = '') => {
        if (current && prev !== current) {
            const { yaMap } = this;
            const mapCenter = this.yaMap.getCenter();
            const regionCenterAndZoom = this.getRegionCenterAndZoom(current);

            if (!isEqual(mapCenter, regionCenterAndZoom.center)) {
                yaMap.setCenter(
                    regionCenterAndZoom.center,
                    regionCenterAndZoom.zoom,
                    {
                        useMapMargin: true
                    }
                );
            }
        }
    };

    private zoomToGroup = (group: DisplayListItem) => {
        const mapCenter = this.yaMap.getCenter();

        if (!isEqual(mapCenter, group.coordinates)) {
            this.setAddressPoint(group);
            this.yaMap.setCenter(group.coordinates, this.yaMap.getZoom(), {
                useMapMargin: true
            });
        }
    };

    private compareSelectedScreens = (
        prevSelected: SelectedScreens,
        selected: SelectedScreens
    ) => {
        const { regions } = this.props;

        regions.forEach((region: DisplayRegion) => {
            const diff =
                xor(prevSelected[region.id], selected[region.id]) || [];

            if (!isEmpty(diff)) {
                this.rebuildSelected(diff, region);
            }
        }, this);

        this.rebuildClusters();
    };

    private rebuildSelected = (diff: string[], region: DisplayRegion) => {
        this.updateScreens(diff, region);

        // TODO определить количество экранов до масс апдейта
        // if (diff.length === 1) {
        //     this.updateScreens(diff, region);
        // } else {
        //     this.buildPoints([region]);
        // }
    };

    private updateScreens = (diff: string[], region: DisplayRegion) => {
        const points = this.props.platforms[region.id];
        const pointsToUpdate: DisplayPoint[] = [];

        points.forEach((point: DisplayPoint) => {
            for (let i = 0; i < point.screens.length; i++) {
                if (diff.includes(point.screens[i].id)) {
                    pointsToUpdate.push(point);

                    break;
                }
            }
        });

        this.updatePoints(pointsToUpdate);
    };

    private updatePoints = (points: DisplayPoint[]) => {
        points.forEach((point: DisplayPoint) => {
            const manager = this.getRegionManagerById(point.regionId);

            manager.objects.setObjectProperties(point.id, {
                selectedScreensPoint: this.getSelectedScreensPoint(point)
            });

            if (manager.objects.balloon.isOpen(point.id)) {
                manager.objects.balloon
                    .getOverlay()
                    // @ts-ignore
                    .then(overlay => overlay.getBalloonLayout())
                    // @ts-ignore
                    .then(layout => layout.rebuild());
            }
        });
    };

    /**
     * Сравнивает пропсы, перерисовывает точки по городам
     */
    private comparePlatforms = (
        prevPlatforms: Platforms,
        platforms: Platforms,
        regions: DisplayRegion[]
    ) => {
        regions.forEach((region: DisplayRegion) => {
            if (
                !isEqual(
                    this.getPlatformsIds(prevPlatforms[region.id]),
                    this.getPlatformsIds(platforms[region.id])
                )
            ) {
                this.buildPoints([region]);
            }
        }, this);
    };

    /**
     * Извлекает все id из переданного массива точек
     */
    private getPlatformsIds = (points: DisplayPoint[]) => {
        return (points || []).map(
            (point: DisplayPoint) => point.id + '-' + point.screens.length
        );
    };

    /**
     * Создает и кэширует инстанс ObjectManager
     * https://tech.yandex.ru/maps/doc/jsapi/2.1/ref/reference/ObjectManager-docpage/
     */
    private getRegionManagerById = (regionId: string): ymaps.ObjectManager => {
        const { regionManager, yaMap } = this;

        if (regionManager[regionId]) {
            return regionManager[regionId];
        } else {
            const manager = new ymaps.ObjectManager({
                clusterize: this.clusterize,
                gridSize: 64,
                clusterDisableClickZoom: false
            });

            manager.objects.options.set({
                preset: 'islands#greenDotIcon',
                iconLayout: this.getIconLayout(),
                balloonContentLayout: this.getBalloonContentLayout(),
                balloonCloseButton: false,
                pointOverlay: 'html#placemark',
                iconPane: 'overlaps'
            });

            manager.clusters.options.set({
                clusterIconLayout: 'default#pieChart',
                clusterIconPieChartStrokeWidth: 3 // TODO унести
            });

            manager.objects.events.add('click', (e: ymaps.Event) => {
                const id = e.get('objectId');
                const groupsId = manager.objects
                    .getById(id)
                    .properties.pointData.screens.map(
                        (screen: { id: string }) => screen.id
                    );

                this.props.onPlatformClick(groupsId);
                this.hideAddressPoint();
            });

            yaMap.geoObjects.add(manager);

            return (regionManager[regionId] = manager);
        }
    };

    private getBalloonContentLayout = () => {
        const { mapType, onBalloonCaptionClick, onScreensSelect } = this.props;
        const templates = this.templates;
        const balloonTemplate = mapType + 'balloon';
        if (templates[balloonTemplate]) {
            return templates[balloonTemplate];
        } else {
            return (templates[
                balloonTemplate
            ] = ymaps.templateLayoutFactory.createClass(
                '<div class="indoor-balloon-template"></div>',
                {
                    build: function() {
                        templates[balloonTemplate].superclass.build.call(this);

                        const data = this.getData();
                        const pointData = data.properties.get('pointData');
                        const selectedScreens = data.properties.get(
                            'selectedScreensPoint'
                        );

                        if (this.getParentElement().parentElement !== null) {
                            this.getParentElement().parentElement.classList.add(
                                'indoor-balloon-content'
                            );
                        }

                        ReactDOM.render(
                            <DisplayMapBalloon
                                mapType={mapType}
                                pointData={pointData}
                                onCaptionClick={onBalloonCaptionClick}
                                onScreensSelect={onScreensSelect}
                                selectedScreens={selectedScreens}
                            />,
                            this.getParentElement().getElementsByClassName(
                                'indoor-balloon-template'
                            )[0]
                        );
                    }
                }
            ));
        }
    };

    private getIconLayout = () => {
        const { mapType } = this.props;
        const templates = this.templates;

        if (templates[mapType]) {
            return templates[mapType];
        } else {
            switch (mapType) {
                case MapType.indoor:
                    return (templates[
                        mapType
                    ] = ymaps.templateLayoutFactory.createClass(
                        '<div class="placemark-indoor-template"</div>',
                        {
                            build: function() {
                                templates[mapType].superclass.build.call(this);

                                const properties = this.getData().properties;

                                ReactDOM.render(
                                    <PlaceMarkIndoor
                                        pointData={properties.pointData}
                                        selectedScreensPoint={
                                            properties.selectedScreensPoint
                                        }
                                    />,
                                    this.getParentElement().getElementsByClassName(
                                        'placemark-indoor-template'
                                    )[0]
                                );
                            }
                        }
                    ));
                case MapType.outdoor:
                    return (templates[
                        mapType
                    ] = ymaps.templateLayoutFactory.createClass(
                        '<div class="placemark-outdoor-template"</div>',
                        {
                            build: function() {
                                templates[mapType].superclass.build.call(this);

                                const properties = this.getData().properties;

                                ReactDOM.render(
                                    <PlaceMarkOutdoor
                                        pointData={properties.pointData}
                                        selectedScreensPoint={
                                            properties.selectedScreensPoint
                                        }
                                    />,
                                    this.getParentElement().getElementsByClassName(
                                        'placemark-outdoor-template'
                                    )[0]
                                );
                            }
                        }
                    ));
                default:
                    return;
            }
        }
    };

    /**
     * Пересчитывает количество выбранных экранов для каждого кластера
     */
    private rebuildClusters = () => {
        const { regionManager } = this;

        forEach(regionManager, manager => {
            manager.clusters.getAll().forEach((cluster: ymaps.Clusterer) => {
                const screenCount = this.getScreenCount(cluster.features);
                const green = screenCount[PointColor.green];
                const blue = screenCount[PointColor.blue];
                const grey = screenCount[PointColor.grey];

                manager.clusters.setClusterProperties(cluster.id, {
                    screenCount: screenCount,
                    iconContent: green + blue + grey,
                    data: [
                        {
                            weight: green,
                            color: PointColor.green
                        },
                        {
                            weight: blue,
                            color: PointColor.blue
                        },
                        {
                            weight: grey,
                            color: PointColor.grey
                        }
                    ]
                });
            });
        });
    };

    private getScreenCount = (features: ymaps.Clusterer[]) => {
        const count = {
            [PointColor.green]: 0,
            [PointColor.blue]: 0,
            [PointColor.grey]: 0
        };

        features.forEach((point: ymaps.Clusterer) => {
            const {
                pointData,
                selectedScreensPoint: selected
            } = point.properties;
            const { screens } = pointData;

            screens.forEach((screen: Screen) => {
                const deletedScreenWeight = screen.deletedScreenWeight || 0;

                if (deletedScreenWeight) {
                    count[PointColor.grey] += deletedScreenWeight;
                }

                if (selected.includes(screen.id)) {
                    count[PointColor.green] +=
                        screen.screenWeight - deletedScreenWeight;
                } else {
                    count[PointColor.blue] +=
                        screen.screenWeight - deletedScreenWeight;
                }
            });
        });

        return count;
    };

    /**
     * Строит и выводит точки на карту
     * https://tech.yandex.ru/maps/doc/jsapi/2.1/ref/reference/ObjectManager-docpage/#method_detail__add-param-objects
     */
    private buildPoints = (
        regions: DisplayRegion[] = this.props.regions
    ): void => {
        const { platforms } = this.props;

        regions.forEach((region: DisplayRegion) => {
            const points = platforms[region.id] || [];
            const regionManager = this.getRegionManagerById(region.id);
            const data = {
                type: 'FeatureCollection',
                features: points.map((point: DisplayPoint) => {
                    return {
                        type: 'Feature',
                        id: point.id,
                        geometry: {
                            type: 'Point',
                            coordinates: point.coordinates
                        },
                        options: {
                            iconImageSize: [34, 34],
                            balloonOffset: [0, -16],
                            hideIconOnBalloonOpen: false,
                            zIndexHover: 801 // на 1 больше чем у метки по адресу
                            // hasBalloon: point.screens.length > 1,
                            // openBalloonOnClick: point.screens.length > 1
                        },
                        properties: {
                            selectedScreensPoint: this.getSelectedScreensPoint(
                                point
                            ),
                            pointData: point
                        }
                    };
                })
            };

            this.yaMap.geoObjects.remove(regionManager); // из за кеширования гео колекции
            regionManager.removeAll(); // при обновлении данных в ней
            regionManager.add(data); // перерисовки объектов(меток) не происходит, по этому
            this.yaMap.geoObjects.add(regionManager); // форсим перерисовку коллекции точек
        }, this);

        this.rebuildClusters();
    };

    private getAddressPoint = () => {
        return this.addressPoint || this.createAddressPoint();
    };

    private createAddressPoint = () => {
        const iconContentLayout = `<div class=${Bem.cls(
            this.block,
            'marker-screens-count'
        )}>{{ properties.iconContent }}</div>`;
        const icon = ymaps.templateLayoutFactory.createClass(iconContentLayout);

        this.pointGeometry = new ymaps.geometry.Point([0, 0]);
        this.addressPoint = new ymaps.GeoObject(
            {
                geometry: this.pointGeometry
            },
            {
                iconLayout: 'default#imageWithContent',
                iconContentLayout: icon,
                iconContentSize: [64, 12],
                iconContentOffset: [0, 18],
                iconImageSize: [64, 64],
                iconImageOffset: [-31, -67],
                iconImageHref: require('./assets/Point.svg'),
                pane: 'overlaps',
                zIndex: 800
            }
        );

        this.yaMap.geoObjects.add(this.addressPoint);

        return this.addressPoint;
    };

    private setAddressPoint = (group: DisplayListItem) => {
        const point = this.getAddressPoint();

        point.properties.set({
            iconContent: group.screens.length
        });
        point.options.set({
            visible: true
        });

        this.pointGeometry.setCoordinates(group.coordinates);
    };

    private hideAddressPoint = () => {
        if (this.addressPoint) {
            this.addressPoint.options.set({
                visible: false
            });
        }
    };

    private getSelectedScreensPoint = (point: DisplayPoint): string[] => {
        const { selectedScreens } = this.props;
        const selectedScreensPoint: string[] = [];

        point.screens.forEach((screen: Screen) => {
            const selected = selectedScreens[point.regionId] || [];

            if (selected.includes(screen.id)) {
                selectedScreensPoint.push(screen.id);
            }
        });

        return selectedScreensPoint;
    };

    private getRegionCenterAndZoom = (regionId: string) => {
        if (this.regionCenter[regionId]) {
            return this.regionCenter[regionId];
        } else {
            const { platforms } = this.props;
            const { bounds: uBounds } = ymaps.util;

            const fromPoints = uBounds.fromPoints(
                platforms[regionId].map(
                    (platform: DisplayPoint) => platform.coordinates
                )
            );

            const centerAndZoom = uBounds.getCenterAndZoom(
                fromPoints,
                this.yaMap.container.getSize()
            );

            return (this.regionCenter[regionId] = {
                center: centerAndZoom.center,
                zoom: this.normalizeZoom(centerAndZoom.zoom)
            });
        }
    };

    private normalizeZoom = (zoom: number) => {
        return zoom > 10 ? 10 : zoom;
    };

    /**
     * Первичная отрисовка карты
     */
    private createMap = (): void => {
        const node = this.mapRef && this.mapRef.current;

        if (!node) {
            throw new Error('createMap: mapRef is undefined');
        }

        const yaMap = (this.yaMap = YandexMapHelper.createMap(
            node,
            {
                type: 'yandex#map',
                zoom: 10,
                controls: ['trafficControl'],
                center: []
            },
            {
                suppressMapOpenBlock: true
            }
        ));

        yaMap.controls.add('zoomControl', {
            position: {
                top: 100,
                right: 10
            }
        });

        // резервируем место для Toolbox
        yaMap.margin.addArea({
            left: 0,
            top: 0,
            width: 430,
            height: '100%'
        });

        // резервируем место для zoomControl
        yaMap.margin.addArea({
            right: 10,
            top: 100,
            width: 80,
            height: 250
        });

        const { regions, drawPolygonsDrawer } = this.props;
        const { bounds: uBounds } = ymaps.util;

        const centerAndZoom = uBounds.getCenterAndZoom(
            uBounds.fromPoints(
                regions.map(
                    (region: DisplayRegion) =>
                        this.getRegionCenterAndZoom(region.id).center
                )
            ),
            yaMap.container.getSize(),
            undefined,
            {
                margin: yaMap.margin.getMargin()
            }
        );

        yaMap.setCenter(
            centerAndZoom.center,
            this.normalizeZoom(centerAndZoom.zoom)
        );

        if (drawPolygonsDrawer) {
            this.forceUpdate();
        }
    };

    /**
     * Если точки будут слишком близко друг к другу -- образуется неразбиваемый кластер
     */
    private checkClusterSize = (newZoom: number) => {
        const { regionManager, clusterize } = this;
        let newClusterize = clusterize;

        newClusterize = newZoom < 19;

        if (newClusterize !== clusterize) {
            forEach(regionManager, manager => {
                manager.options.set({
                    clusterize: newClusterize
                });
            });

            this.clusterize = newClusterize;
        }
    };

    private initMapEvents = () => {
        const _this = this;

        this.yaMap.events.add('boundschange', (e: ymaps.IEvent) => {
            _this.checkClusterSize(e.get('newZoom'));

            setTimeout(() => {
                _this.rebuildClusters();
            }, 0);
        });

        this.yaMap.events.add('click', () => {
            this.yaMap.balloon.close();
        });

        this.props.onMapLoaded(true);
    };
}
